import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../redux";
import { Button, DropdownField, InputField, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { PageLayout } from "../../components/PageLayout";
import { AgentEnvironmentDropdownField } from "../../components/AgentEnvironmentDropdownField";
import { Dialog } from "../../components/base/Dialog";
import { DataTable } from "../../components/DataTable";
import { useApiRequest } from "../../hooks/useApiRequest";
import { SimulationAgent, simulationAgentActions } from "../../redux/reducers/simulationAgentSlice";
import { agentActions } from "../../redux/reducers/agentSlice";
import { agentEnvironmentActions } from "../../redux/reducers/agentEnvironmentSlice";

export const AgentSimulationsPage = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const agentEnvironments = useAppSelector((state) => Object.values(state.agentEnvironment.entities).filter((env) => env.agentId === agentId));
  const simulationAgents = useAppSelector((state) => Object.values(state.simulationAgent.entities));
  const simulationAgentsIsLoading = useAppSelector((state) => state.simulationAgent.isLoading);
  const agents = useAppSelector((state) => Object.values(state.agent.entities));
  const agentsIsLoading = useAppSelector((state) => state.agent.isLoading);

  const defaultEnvironmentId = useMemo(() => (agentEnvironments.length > 0 ? agentEnvironments[0].id : ""), [agentEnvironments]);
  const [agentEnvironmentId, setAgentEnvironmentId] = useState<string>(defaultEnvironmentId);
  const [selectedAgentId, setSelectedAgentId] = useState<string>(agentId || "");

  // Get environments for the selected agent in simulation config
  const selectedAgentEnvironments = useAppSelector((state) => Object.values(state.agentEnvironment.entities).filter((env) => env.agentId === selectedAgentId));
  const selectedAgentDefaultEnvironmentId = useMemo(
    () => (selectedAgentEnvironments.length > 0 ? selectedAgentEnvironments[0].id : ""),
    [selectedAgentEnvironments],
  );
  const [lastRunSimulationAgentId, setLastRunSimulationAgentId] = useState<string | null>(null);
  const [isSimulationConfigDialogOpen, setIsSimulationConfigDialogOpen] = useState(false);
  const [selectedSimulationAgent, setSelectedSimulationAgent] = useState<SimulationAgent | null>(null);
  const [numberOfSimulations, setNumberOfSimulations] = useState<number>(1);
  const [simulationEnvironmentId, setSimulationEnvironmentId] = useState<string>(defaultEnvironmentId);

  const simulationItems = useMemo(() => {
    return simulationAgents.map((sim) => ({
      ...sim,
      description: sim.systemPrompt || "",
    }));
  }, [simulationAgents]);

  useEffect(() => {
    if (!agentEnvironmentId && defaultEnvironmentId) {
      setAgentEnvironmentId(defaultEnvironmentId);
    }
  }, [defaultEnvironmentId, agentEnvironmentId]);

  useEffect(() => {
    if (!simulationEnvironmentId && defaultEnvironmentId) {
      setSimulationEnvironmentId(defaultEnvironmentId);
    }
  }, [defaultEnvironmentId, simulationEnvironmentId]);

  // Load environments when selected agent changes
  useEffect(() => {
    if (selectedAgentId) {
      dispatch(agentEnvironmentActions.get({ agentId: selectedAgentId }));
    }
  }, [selectedAgentId, dispatch]);

  // Update environment when selected agent changes
  useEffect(() => {
    if (selectedAgentDefaultEnvironmentId) {
      setSimulationEnvironmentId(selectedAgentDefaultEnvironmentId);
    }
  }, [selectedAgentId, selectedAgentDefaultEnvironmentId]);

  useEffect(() => {
    dispatch(agentActions.get({}));
    dispatch(simulationAgentActions.get({}));
  }, []);

  const runSimulationAgentRequest = useApiRequest<{ data: { id: string } }>();

  const openSimulationConfigDialog = (simulationAgent: SimulationAgent) => {
    setSelectedSimulationAgent(simulationAgent);
    setNumberOfSimulations(1);
    setSelectedAgentId(agentId || "");
    setSimulationEnvironmentId(defaultEnvironmentId);
    setIsSimulationConfigDialogOpen(true);
  };

  const closeSimulationConfigDialog = () => {
    setIsSimulationConfigDialogOpen(false);
    setSelectedSimulationAgent(null);
    setNumberOfSimulations(1);
    setSelectedAgentId(agentId || "");
  };

  const handleRunSimulation = async () => {
    if (!selectedSimulationAgent || !selectedAgentId) {
      return;
    }

    setLastRunSimulationAgentId(selectedSimulationAgent.id);

    const { error: runError } = await runSimulationAgentRequest.fetch(`/v1/simulation-agents/${selectedSimulationAgent.id}/run`, {
      method: "POST",
      body: {
        numberOfSimulations,
        agentId: selectedAgentId,
        agentEnvironmentId: simulationEnvironmentId,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!runError) {
      setLastRunSimulationAgentId(selectedSimulationAgent.id);
      closeSimulationConfigDialog();
    }
  };

  const onClickItem = (id: string) => {
    navigate(`/agents/simulations/${id}`);
  };

  const onClickCreate = () => {
    navigate("/agents/simulations/new");
  };

  const filterSimulations = (searchQuery: string, item: Record<string, unknown>) => {
    const sim = item as SimulationAgent & { description: string };
    const simName = sim.name?.toLowerCase() || "";
    const simDescription = sim.description?.toLowerCase() || "";

    return simName.includes(searchQuery.toLowerCase()) || simDescription.includes(searchQuery.toLowerCase());
  };

  const sortSimulations = (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const simA = a as SimulationAgent;
    const simB = b as SimulationAgent;

    return new Date(simB.updatedAt).getTime() - new Date(simA.updatedAt).getTime();
  };

  return (
    <>
      <PageLayout
        title="Simulations"
        description="Create and manage simulation agents to test different prompts and behaviors in safe environments."
      >
        <DataTable
          key={simulationItems.length}
          entityName="simulation"
          emptyMessage={`Create your first simulation agent to get started.\nSimulation agents can test different prompts and behaviors.`}
          filter={filterSimulations}
          sort={sortSimulations}
          isLoading={simulationAgentsIsLoading}
          onClickCreate={onClickCreate}
          items={simulationItems}
          onRowClick={onClickItem}
          showIcons={true}
          iconName="users"
          timestampLabel="Last Updated"
          createStyle="row"
          createRowLabel="Create simulation"
          hideEmptyMessage
        />
      </PageLayout>

      <Dialog
        isOpen={isSimulationConfigDialogOpen}
        onClose={closeSimulationConfigDialog}
        title="Run Simulations"
        size="md"
        actions={
          <>
            <Button
              variant="neutral"
              size="auto"
              onClick={closeSimulationConfigDialog}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              size="auto"
              onClick={handleRunSimulation}
              disabled={
                !selectedSimulationAgent || !selectedAgentId || numberOfSimulations < 1 || !simulationEnvironmentId || runSimulationAgentRequest.isLoading
              }
            >
              {runSimulationAgentRequest.isLoading ? (
                <Icon
                  name="loader"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="play"
                  size={14}
                />
              )}
              Start
            </Button>
          </>
        }
      >
        <View className="space-y-4 p-4">
          {selectedSimulationAgent && (
            <View className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 mr-1">Simulation Agent</Text>
              <Text className="text-base font-semibold text-gray-900 dark:text-white">{selectedSimulationAgent?.name}</Text>
            </View>
          )}

          <InputField
            label="Number of Simulations"
            placeholder="Enter number of simulations to run"
            value={numberOfSimulations.toString()}
            onChange={(value) => setNumberOfSimulations(Math.max(1, parseInt(value) || 1))}
            type="number"
          />
          <DropdownField
            label="Agent"
            options={agents.map((agent) => ({
              value: agent.id,
              label: agent.name,
            }))}
            value={selectedAgentId}
            onChange={setSelectedAgentId}
            placeholder="Select an agent"
            readOnly={agentsIsLoading}
          />
          <AgentEnvironmentDropdownField
            label="Environment"
            agentEnvironments={selectedAgentEnvironments}
            value={simulationEnvironmentId}
            onChange={setSimulationEnvironmentId}
          />
        </View>
      </Dialog>
    </>
  );
};
